import {
  IntegrationConfig,
  IntegrationFeature,
  SourceAuthProvider,
  SourceGenericAuthTypes,
  SourceType,
} from './Integration.interface';

// `GET`, `HEAD` and `QUERY` are opt-in per source and are not part of the default set
// applied in `SourceModel` — some platforms (Baselinker) only ever send HEAD, and
// `QUERY` (RFC 10008) is new enough that no source should begin accepting it silently.
export const source_allowed_http_methods = [
  'GET',
  'HEAD',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
  'QUERY',
] as const;
export type SourceAllowedHTTPMethod = (typeof source_allowed_http_methods)[number];
export const source_custom_response_content_types = ['json', 'text', 'xml'] as const;
export type SourceCustomResponseContentType = (typeof source_custom_response_content_types)[number];

export interface SourceCustomResponse {
  content_type: SourceCustomResponseContentType;
  body: string;
}

export interface Source {
  id: string;
  team_id: string;
  name: string;
  description: string | null;
  type: SourceType;
  url: string;
  verification: {
    type: SourceAuthProvider;
    configs?: IntegrationConfig;
  } | null;
  allowed_http_methods: SourceAllowedHTTPMethod[];
  custom_response: SourceCustomResponse | null;
  trim_payload?: boolean | null;
  disabled_at?: string | null;
  deleted_at: string | null;
  updated_at: string;
  created_at: string;

  // Deprecated
  integration_id?: string;
  integration?: {
    id: string;
    label: string;
    provider: SourceAuthProvider;
    features: IntegrationFeature[];
  };
}

export interface APISource
  extends Omit<
    Source,
    'verification' | 'allowed_http_methods' | 'custom_response' | 'integration' | 'integration_id'
  > {
  type: SourceType;
  authenticated: boolean;
  config: SourceConfig;
}

export type SourceConfig = {
  auth_enabled: boolean;
  auth_type?: SourceAuthProvider; // SourceType is because of backward compatibility
  auth?: IntegrationConfig;
  allowed_http_methods?: SourceAllowedHTTPMethod[] | null;
  custom_response?: SourceCustomResponse | null;
  trim_payload?: boolean | null;
};

export interface SourceCreateInput extends Pick<Source, 'name'> {
  id?: string;
  team_id: string;
  description?: string | null;
  type: SourceType;
  integration_id?: string;
  allowed_http_methods?: SourceAllowedHTTPMethod[] | null;
  custom_response?: SourceCustomResponse | null;
  trim_payload?: boolean | null;
  verification?: {
    type: SourceAuthProvider;
    configs: IntegrationConfig;
  } | null;
}

export interface SourceUpdateInput
  extends Partial<Pick<Source, 'name' | 'description' | 'custom_response' | 'integration_id'>> {
  type?: SourceType;
  disabled_at?: string;
  deleted_at?: string;
  allowed_http_methods?: SourceAllowedHTTPMethod[];
  trim_payload?: boolean | null;
  verification?: {
    type: SourceAuthProvider;
    configs: IntegrationConfig;
  } | null;
}
